<template>
  <v-container class="modern-calendar-view pb-12">
    <!-- Premium Global Header (Master Pattern) -->
    <v-row class="mb-6 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="44" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white" small>fas fa-calendar-check</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text d-flex align-center lh-1">
            Calendario de Compromisos
          </div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Control de entregas y metas de producción en metros cuadrados</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center gap-3">
        <v-select
          v-model="line"
          :items="getLines"
          item-text="name"
          item-value="id"
          placeholder="Todas las Líneas"
          outlined dense hide-details
          clearable
          class="modern-select bg-white"
          style="width: 200px"
          @change="handleLineChange"
        />
      </div>
    </v-row>

    <!-- Metrics Dashboard -->
    <v-row class="mb-8 px-2">
      <v-col cols="12" md="4">
        <v-card outlined class="modern-card metric-card shadow-sm border-left-indigo pa-4">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="caption grey--text font-weight-black text-uppercase tracking-widest">Comprometido</span>
            <v-icon color="indigo lighten-3" small>fas fa-file-contract</v-icon>
          </div>
          <div class="d-flex align-baseline">
            <span class="text-h3 font-weight-black indigo--text mr-1">{{totalCompromised}}</span>
            <span class="caption grey--text font-weight-bold">mts²</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="modern-card metric-card shadow-sm border-left-success pa-4">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="caption grey--text font-weight-black text-uppercase tracking-widest">Terminado</span>
            <v-icon color="success lighten-3" small>fas fa-check-double</v-icon>
          </div>
          <div class="d-flex align-baseline">
            <span class="text-h3 font-weight-black success--text mr-1">{{totalFinished}}</span>
            <span class="caption grey--text font-weight-bold">mts²</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="modern-card metric-card shadow-sm border-left-error pa-4">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="caption grey--text font-weight-black text-uppercase tracking-widest">Pendiente</span>
            <v-icon color="error lighten-3" small>fas fa-hourglass-half</v-icon>
          </div>
          <div class="d-flex align-baseline">
            <span class="text-h3 font-weight-black error--text mr-1">{{totalPending}}</span>
            <span class="caption grey--text font-weight-bold">mts²</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Calendar Interface -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <div class="px-6 py-4 border-bottom d-flex align-center bg-white">
        <v-btn icon outlined small fab color="primary" @click="$refs.calendar.prev()" :disabled="loading" class="mr-4">
          <v-icon small>fas fa-chevron-left</v-icon>
        </v-btn>
        <span class="text-h5 primary--text font-weight-black text-uppercase tracking-wider flex-grow-1 text-center">
          {{currentMonth}}
        </span>
        <v-btn icon outlined small fab color="primary" @click="$refs.calendar.next()" :disabled="loading" class="ml-4">
          <v-icon small>fas fa-chevron-right</v-icon>
        </v-btn>
      </div>

      <v-sheet height="700" class="position-relative">
        <v-overlay absolute v-if="loading" color="white" opacity="0.8">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        </v-overlay>

        <v-calendar
          ref="calendar"
          v-model="value"
          :weekdays="weekday"
          :type="type"
          :event-height="24"
          :events="events"
          :event-more="false"
          :event-overlap-mode="mode"
          :event-overlap-threshold="100"
          :event-color="getEventColor"
          class="modern-calendar"
          @change="changeinCalendar"
          @click:event="showEvent"
        >
          <template v-slot:event="{ event }">
            <div class="d-flex align-center justify-center fill-height px-1">
              <v-progress-circular
                v-if="event.loading"
                indeterminate
                dark
                width="2"
                size="12"
              ></v-progress-circular>
              <div v-else class="caption font-weight-black tracking-tighter lh-1 white--text">
                {{event.value}}
              </div>
            </div>
          </template>
        </v-calendar>
      </v-sheet>
    </v-card>

    <!-- Detail Dialog (Detail Pattern) -->
    <v-dialog v-model="selectedOpen" max-width="1200" overlay-opacity="0.9" overlay-color="#1A1F2E">
      <v-card outlined class="modern-card overflow-hidden">
        <v-sheet :color="selectedEvent.color" class="px-6 py-4 d-flex align-center">
          <v-icon color="white" class="mr-3">
            {{ selectedEvent.type == 'compromised' ? 'fas fa-file-contract' : (selectedEvent.type == 'finished' ? 'fas fa-check-double' : 'fas fa-hourglass-half') }}
          </v-icon>
          <div>
            <div class="text-h6 font-weight-black white--text text-uppercase tracking-wider lh-1">
              {{ selectedEvent.type == 'compromised' ? 'Comprometido' : (selectedEvent.type == 'finished' ? 'Terminado' : 'Pendiente') }}
            </div>
            <div class="caption white--text opacity-80 font-weight-bold uppercase tracking-widest mt-1">
              Detalle del {{ selectedEvent.start | moment('DD [de] MMMM, YYYY') }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <vue-json-to-csv
            :json-data="selectedEventProducts"
            :labels="labels"
            :csv-title="selectedEventTitle"
            :separator="';'"
          >
            <v-btn dark depressed color="rgba(255,255,255,0.15)" class="font-weight-black px-4 rounded-lg">
              <v-icon x-small class="mr-2">fas fa-file-export</v-icon>
              Exportar CSV
            </v-btn>
          </vue-json-to-csv>
          <v-btn icon color="white" class="ml-4" @click="selectedOpen = false">
            <v-icon small>fas fa-times</v-icon>
          </v-btn>
        </v-sheet>

        <v-data-table
          :headers="headersSelectedEvent"
          :items="selectedEventProducts"
          :items-per-page="10"
          class="modern-table"
          :footer-props="{
            'items-per-page-options': [10, 20, 50]
          }"
        >
          <template v-slot:item.work_order="{ item }">
            <show-work-order :work_order_id="item.work_order" class="OT-link font-weight-black">
              OT {{item.work_order}}
            </show-work-order>
          </template>
          
          <template v-slot:item.customer="{ item }">
            <div class="caption font-weight-bold text-truncate" style="max-width: 250px">{{item.customer}}</div>
          </template>

          <template v-slot:item.compromised="{ item }">
            <div class="text-end font-weight-black indigo--text">{{item.compromised | number('0,0.0')}}</div>
          </template>
          
          <template v-slot:item.finished="{ item }">
            <div class="text-end font-weight-black success--text">{{item.finished | number('0,0.0')}}</div>
          </template>
          
          <template v-slot:item.pending="{ item }">
            <div class="text-end font-weight-black error--text">{{item.pending | number('0,0.0')}}</div>
          </template>
        </v-data-table>
      </v-card>
    </v-dialog>

    <!-- Generic Work Order Detail Dialog -->
    <v-dialog v-model="display" max-width="1200" overlay-opacity="0.9" overlay-color="#1A1F2E">
      <v-card outlined class="modern-card pa-4">
        <work-order-card :work_order="workOrderOnDisplay" @close="display=false" :loading="workOrderOnDisplayLoading"/> 
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment'
import numeral from 'numeral'
import { fetchMontlyCompromised, fetchMontlyCompromisedDetail } from '@/api/reports'
import notifications from '@/utils/notifications'
import NumberCard from '@/components/utils/NumberCard.vue'
import VueJsonToCsv from 'vue-json-to-csv'
export default {
  name:'CompromiseCalendar2',
  components:{
    VueJsonToCsv,
    NumberCard
  },
  data() {
    return {
      
      currentMonth: '',
      workOrders:[],
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      type: 'month',
      types: ['month', 'week', 'day', '4day'],
      mode: 'stack',
      modes: ['stack', 'column'],
      weekday: [1, 2, 3, 4, 5, 6, 0],
      weekdays: [
        { text: 'Lunes - Domingo', value: [1, 2, 3, 4, 5, 6, 0] },
        { text: 'Lunes - Viernes', value: [1, 2, 3, 4, 5] },
      ],
      value: '',
      events: [],
      colors: ['blue', 'indigo', 'deep-purple', 'cyan', 'green', 'orange', 'grey darken-1','orange darken-4'],
      names: ['Meeting', 'Holiday', 'PTO', 'Travel', 'Event', 'Birthday', 'Conference', 'Party'],
      workOrderOnDisplay:{},
      workOrderOnDisplayLoading:false,
      display:false,
      loading:false,
      line:null,
      headersSelectedEvent:[
        {text:'OT',value:'work_order'},
        {text:'Cliente',value:'customer'},
        {text:'Proyecto',value:'title'},
        {text:'Total',value:'compromised',align:'right'},
        {text:'Terminados',value:'finished',align:'right'},
        {text:'Pendientes',value:'pending',align:'right'},
      ],
      productionCalendar:false,
      labels:{ 
        work_order: { title: 'OT' },
        customer:{title:'Cliente'},
        title:{title:'Proyecto'},
        seller:{title:'Vendedor'},
        compromised:{title:'Total'},
        finished:{title:'Terminado'},
        pending:{title:'Pendiente'},
      },
      totalCompromised:0,
      totalFinished:0,
      totalPending:0,
      eventMonth:0,
      eventYear:0
    }
  },
  methods:{
      getEventColor (event) {
        return event.color
      },
      showEvent ({ nativeEvent, event }) {
        const open = async () => {
          this.selectedEvent = event
          this.selectedEvent.loading = true
          const workOrders = await this.fetchDetails(event.start)
          .finally(()=>{
            this.selectedEvent.loading=false
          })
          if(workOrders.length>0){
            this.selectedEvent={
              type:event.type,
              workOrders:workOrders,
              start:event.start,
              color:event.color
            }
          }

          this.selectedElement = nativeEvent.target
          requestAnimationFrame(() => requestAnimationFrame(() => this.selectedOpen = true))
        }

        if (this.selectedOpen) {
          this.selectedOpen = false
          requestAnimationFrame(() => requestAnimationFrame(() => open()))
        } else {
          open()
        }

        nativeEvent.stopPropagation()
      },
      changeinCalendar(event){
        this.eventMonth=event.start.month,
        this.eventYear=event.start.year
        this.currentMonth=moment(event.start.date).format('MMMM').toUpperCase()+' - '+event.start.year
        this.fetchData()
      },
      async fetchData(){
        const DECIMAL_FACTOR=10
        this.events=[]
        
        this.loading=true
        const year=this.eventYear
        const month=this.eventMonth
        let query=`year=${year}&month=${month}`
        if(this.line) query = query+`&line=${this.line}`
        
        const events = await fetchMontlyCompromised(query)
        .catch(error=>{
          console.log(error)
          notifications.error(error)
        })
        .finally(()=>{
          this.loading=false
        })
        
        if(events.length>0){
          events.forEach(ev=>{
            this.events.push({
              name:`${ev?.compromised/DECIMAL_FACTOR}`,
              value: ev?.compromised/DECIMAL_FACTOR,
              start: ev?.date,
              color:'indigo',
              type:'compromised',
              loading:false

            })
            this.events.push({
              name:`${ev?.finished/DECIMAL_FACTOR}`,
              value: ev?.finished/DECIMAL_FACTOR,
              start: ev?.date,
              color:'green',
              type:'finished',
              loading:false
            })
            this.events.push({
              name:`${ev?.pending/DECIMAL_FACTOR}`,
              value: ev?.pending/DECIMAL_FACTOR,
              start: ev?.date,
              color:'error',
              type:'pending',
              loading:false
            })
          })
        }

        // Calculo las sumas de cada uno para las tarjetas
        this.totalCompromised=numeral(events.reduce((acc,val)=>{return acc+val.compromised/DECIMAL_FACTOR},0)).format('0,0[.]0')
        this.totalFinished=numeral(events.reduce((acc,val)=>{return acc+val.finished/DECIMAL_FACTOR},0)).format('0,0[.]0')
        this.totalPending=numeral(events.reduce((acc,val)=>{return acc+val.pending/DECIMAL_FACTOR},0)).format('0,0[.]0')
        
      },
      async fetchDetails(date){
        const DECIMAL_FACTOR=10
        let query = `date=${date}`
        console.log(date)

        if(this.line) query=query+`&line=${this.line}`

        const workOrders=await fetchMontlyCompromisedDetail(query)
        .catch(error=>{
          console.log(error)
          notifications.error(error)
          
        })
        .finally(()=>{
          
        })
        console.log("WorkOrders",workOrders)
        if(workOrders?.length>0){
          return workOrders.map(w=>({
            ...w,
            compromised:w.compromised/DECIMAL_FACTOR,
            finished:w.finished/DECIMAL_FACTOR,
            pending:w.pending/DECIMAL_FACTOR,
          }))
        }
        else {
          return []
        }

      },
      handleLineChange(){
        this.fetchData()
      }
  },
  computed:{
    getLines(){
      return this.$store.getters.getLines
    },
    selectedEventProducts(){
      const { type, workOrders } = this.selectedEvent
      if (!workOrders) return []
      if (type == 'finished') return workOrders.filter(wO => wO.pending == 0)
      if (type == 'pending') return workOrders.filter(wO => wO.pending != 0)
      return workOrders
    },
    selectedEventTitle(){
      if(!this.selectedEvent || !this.selectedEvent.start) return 'Export'
      const date = this.$options.filters.moment(this.selectedEvent.start, 'DD-MM-YYYY')
      if (this.selectedEvent.type == 'compromised') return 'Comprometido_' + date
      if (this.selectedEvent.type == 'finished') return 'Terminado_' + date
      if (this.selectedEvent.type == 'pending') return 'Pendiente_' + date
      return 'Export_' + date
    }
  },
}
</script>

<style lang="scss" scoped>
.modern-calendar-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }

.modern-select >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }

.metric-card {
  transition: transform 0.2s;
  &:hover { transform: translateY(-4px); }
  &.border-left-indigo { border-left: 4px solid #3f51b5 !important; }
  &.border-left-success { border-left: 4px solid #4CAF50 !important; }
  &.border-left-error { border-left: 4px solid #f44336 !important; }
}

.modern-calendar {
  border: none !important;
  ::v-deep {
    .v-calendar-weekly__head-weekday {
      padding: 12px;
      text-transform: uppercase;
      font-size: 0.7rem;
      letter-spacing: 1.5px;
      font-weight: 900;
      color: #90a4ae;
      background-color: #f8fbfc;
      border-bottom: 1px solid #eee;
    }
    .v-calendar-weekly__day {
      transition: background-color 0.2s;
      &:hover { background-color: #fcfdfe; }
    }
    .v-calendar-weekly__day-label .v-btn {
      height: 24px !important;
      width: 24px !important;
      font-weight: 900;
      font-size: 0.75rem;
    }
    .v-event {
      border-radius: 4px !important;
      margin-bottom: 2px !important;
      box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    }
    .v-past { background-color: #f5f5f5 !important; opacity: 0.6; }
  }
}

.modern-table >>> .v-data-table-header th { white-space: nowrap; height: 56px !important; text-transform: uppercase; font-size: 0.7rem !important; letter-spacing: 1.5px; font-weight: 900 !important; background-color: #f8fbfc !important; color: #607d8b !important; }
.modern-table >>> td { padding: 12px 16px !important; border-bottom: 1px solid #f0f3f5 !important; }

.OT-link { text-decoration: none; color: var(--v-primary-base); transition: opacity 0.2s; &:hover { opacity: 0.7; } }

.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 2px; }
.tracking-wider { letter-spacing: 1px; }
.tracking-tighter { letter-spacing: -0.5px; }

.premium-tooltip { background: rgba(33, 33, 33, 0.95) !important; backdrop-filter: blur(4px); border-radius: 12px !important; border: 1px solid rgba(255,255,255,0.1); }
</style>